import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Grid, Box, RadioGroup } from '@material-ui/core';
import { repositories } from 'api';
import { checkoutOperations } from 'store/ducks/checkout';
import { CartShopStats, ShopCartItem } from 'store/ducks/shopCart/types';
import { CheckoutValues } from 'store/ducks/checkout/types';
import { sumStatsInCartShopItems } from 'store/ducks/shopCart/selectors';
import Button from 'common/components/Button';
import ErrorPanel from 'common/components/ErrorPanel';
import { StoreBasicDataApiStructure } from 'common/types';
import { AvailabilitySchema } from './common/validation';
import { routes } from '../../common/routes';
import OrderType from './components/OrderType';
import Summary from './components/Summary';
import Product from './components/Product';
import { DEFAULT_GRID_CONTAINER_SPACING, CHANNEL } from 'common/constants';
import { shopCartOperations } from 'store/ducks/shopCart';
import { CheckoutType } from 'api/repositories';
import { ORDER_TYPE, FormFieldNames } from '../../common/constants';
import CheckoutRepository from 'api/repositories/Checkout';
import { getCartToken } from '../../common/functions';
import { makeCartStyles } from 'styles/pages/checkout';
import Icon from 'common/components/Icon';

type Props = {
    carts: any;
    changeProductQuantity: Function;
    checkoutValues: CheckoutValues;
    isPromoter: boolean;
    items: Array<ShopCartItem>;
    itemsStats: CartShopStats;
    pendingData: {
        [key: string]: boolean;
    };
    removeItemFromShopCart: Function;
    setCheckoutValues: Function;
};

type MappingType = {
    checkout: {
        data: StoreBasicDataApiStructure<CheckoutType>;
        values: CheckoutValues;
    };
    me: StoreBasicDataApiStructure<repositories.MeType>;
    pendingData: {
        [key: string]: boolean;
    };
    shopCart: Array<ShopCartItem>;
    simpleProduct: StoreBasicDataApiStructure<repositories.SimpleProductType>;
};

const Cart: React.FC<Props> = ({
    carts,
    changeProductQuantity,
    checkoutValues,
    isPromoter,
    items,
    itemsStats,
    pendingData,
    removeItemFromShopCart,
    setCheckoutValues,
}) => {
    const { push } = useHistory();

    const { control, errors, handleSubmit } = useForm({
        validationSchema: AvailabilitySchema,
    });

    const cartToken = getCartToken(carts, CHANNEL);

    const errorsCount = Object.keys(errors).length;
    const errorsExist = useMemo(() => errorsCount > 0, [errorsCount]);
    const productsCart = items.length;

    const loading = pendingData['CART_ITEM_QUANTITY'];

    const onSubmit = useCallback(
        (values) => {
            setCheckoutValues({ ...checkoutValues, ...values });

            new CheckoutRepository()
                .put({
                    data: { type: values.orderType },
                    url: `checkout/${cartToken}/type`,
                })
                .then(() => push(routes.ORDER));
        },
        [cartToken, checkoutValues, push, setCheckoutValues],
    );

    const { button, icon, summaryWrapper, orderTypeWrapper } = makeCartStyles();

    return productsCart ? (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
                as={RadioGroup}
                control={control}
                defaultValue={checkoutValues.orderType || ''}
                name={ORDER_TYPE}
            >
                <Box className={orderTypeWrapper}>
                    <Grid container justify="center" spacing={2}>
                        <Grid item xs={12} sm={12} md={5} lg={4}>
                            <OrderType error={errorsExist} type={FormFieldNames.SCHOOL} />
                        </Grid>
                        <Grid item xs={12} sm={12} md={5} lg={4}>
                            <OrderType error={errorsExist} type={FormFieldNames.COUNSEL_PARENTS} />
                        </Grid>
                        <Grid item xs={8}>
                            <Box display="flex" justifyContent="center">
                                <ErrorPanel errors={errors} name={ORDER_TYPE} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Controller>
            <Grid container spacing={DEFAULT_GRID_CONTAINER_SPACING}>
                <Grid item xs={12} md={8} lg={9}>
                    <Box marginTop={4}>
                        {items.map((item) => (
                            <Product
                                key={item.code}
                                changeProductQuantity={changeProductQuantity}
                                isPromoter={isPromoter}
                                item={item}
                                loading={loading}
                                removeItemFromShopCart={removeItemFromShopCart}
                            />
                        ))}
                    </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                    <Box className={summaryWrapper}>
                        <Box position="relative" marginTop={4} marginBottom={2}>
                            <Summary loading={loading} sumPrice={itemsStats.sumPrice} />
                        </Box>
                        <Box
                            alignItems="center"
                            display="flex"
                            justifyContent="flex-end"
                            marginTop={3.5}
                        >
                            <Button
                                className={button}
                                disabled={errorsExist || loading}
                                type="submit"
                            >
                                Złóż zamówienie{' '}
                                <span className={icon}>
                                    <Icon.ArrowWhiteRight />
                                </span>
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </form>
    ) : null;
};

const mapStateToProps = (state: MappingType) => ({
    ...state.simpleProduct,
    carts: state.me.data?.carts,
    checkoutValues: state.checkout.values,
    isPromoter: state.me?.data?.isPromoter,
    items: state.shopCart,
    itemsStats: sumStatsInCartShopItems(state),
    pendingData: state.pendingData,
});

const mapDispatchToProps = {
    changeProductQuantity: shopCartOperations.changeProductQuantity,
    removeItemFromShopCart: shopCartOperations.removeItemFromShopCart,
    setCheckoutValues: checkoutOperations.setCheckoutValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
